/* eslint-disable @typescript-eslint/no-unused-vars */
import { IntegrationAppProvider, useIntegrationApp } from "@membranehq/react";
import { useMemo, useState, useEffect } from "react";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { AlertTriangle, Check, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import GmailNewFlow from "./new-flow/GmailNewFlow";
import { hyperengageClient } from "@hyperengage/sdk-js";
import {
    queryClient,
    useAIMemoryObjects,
    useCreateSyncConfiguration,
    useCurrentUser,
    useGmailIntegration,
    useMemberIntegrationsMe,
    useSyncConfigurations,
    useUpdateMemberIntegration,
    useUpdateSyncConfiguration,
    useWorkspaceMembers,
} from "../../../hooks/QueryHooks";
import { useQuery } from "@tanstack/react-query";
import { getProvider } from "../../../integrations/providers";
import { toast } from "sonner";
import { generateIntegrationAppToken } from "../../../services/integrationAppEndpoint";
import { triggerBestEffortInitialSync, notifyBackendOfConnection } from "../../../integrations/integrationAppPostConnect";
import { activateInboundSyncForProvider } from "../../../integrations/activateInboundSync";
import { updateMemberIntegration, createMemberIntegration, getMemberIntegrationsMe } from "../../../services/memberIntegrationsEndpoint";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";
import IntegrationDetailShell from "../../home/settings/integrations/components/IntegrationDetailShell";
import { cn } from "../../../lib/utils";

interface GmailPageContentProps {
    isConnected: boolean;
    connectedAt: string | null;
    integrationLoading: boolean;
    integrationId: string;
    onConnect: () => void;
    connectionData: any;
    setConnectionData: (data: any) => void;
    setIsConnected: (connected: boolean) => void;
    setConnectedAt: (timestamp: string | null) => void;
    setIntegrationLoading: (loading: boolean) => void;
}

const normalizeProvider = (provider: string | undefined) => (provider || '').toLowerCase().replace(/_/g, '-');

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

const DAY_OPTIONS: { value: DayKey; label: string }[] = [
    { value: "mon", label: "Mon" },
    { value: "tue", label: "Tue" },
    { value: "wed", label: "Wed" },
    { value: "thu", label: "Thu" },
    { value: "fri", label: "Fri" },
    { value: "sat", label: "Sat" },
    { value: "sun", label: "Sun" },
];

const parseCsv = (value: string) => value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const hyperengage = hyperengageClient({
    key: "hjs_c0b0fcdbd0e15c286439ab27a6ca57e9",
    workspace_key: "hgS7uKB67w",
    tracking_host: "https://staging-events.hyperengage.io",
});

type GmailSettingRowProps = {
    label: string;
    description?: string;
    children: React.ReactNode;
};

const GmailSettingRow = ({ label, description, children }: GmailSettingRowProps) => (
    <div className="flex items-start justify-between gap-6 py-3">
        <div className="min-w-0">
            <p className="text-[13px] text-foreground">{label}</p>
            {description ? <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p> : null}
        </div>
        <div className="shrink-0">{children}</div>
    </div>
);

const GmailMemberSettings = ({ isConnected }: { isConnected: boolean }) => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);
    const updateMemberIntegrationMutation = useUpdateMemberIntegration();

    const {
        data: memberIntegrations,
        isFetching: memberIntegrationsFetching,
        refetch: refetchMemberIntegrations,
    }: any = useMemberIntegrationsMe({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    });

    const gmailMemberIntegrations = useMemo(() => (memberIntegrations || [])
        .filter((mi: any) => normalizeProvider(mi?.provider) === "gmail"), [memberIntegrations]);

    const [selectedMemberIntegrationId, setSelectedMemberIntegrationId] = useState<string | null>(null);
    const selectedMemberIntegration = useMemo(() => (
        gmailMemberIntegrations.find((mi: any) => mi?.id === selectedMemberIntegrationId)
        || gmailMemberIntegrations.find((mi: any) => mi?.is_default_for_member)
        || gmailMemberIntegrations[0]
        || null
    ), [gmailMemberIntegrations, selectedMemberIntegrationId]);

    useEffect(() => {
        if (!gmailMemberIntegrations?.length) {
            setSelectedMemberIntegrationId(null);
            return;
        }
        const nextDefaultId = (
            gmailMemberIntegrations.find((mi: any) => mi?.id === selectedMemberIntegrationId)?.id
            || gmailMemberIntegrations.find((mi: any) => mi?.is_default_for_member)?.id
            || gmailMemberIntegrations[0]?.id
        );
        if (nextDefaultId && nextDefaultId !== selectedMemberIntegrationId) {
            setSelectedMemberIntegrationId(nextDefaultId);
        }
    }, [gmailMemberIntegrations, selectedMemberIntegrationId]);

    const [activeTab, setActiveTab] = useState<"general" | "sending" | "safety">("general");
    const [isDefaultForMember, setIsDefaultForMember] = useState<boolean>(false);
    const [useGmailSignature, setUseGmailSignature] = useState<boolean>(false);
    const [signatureText, setSignatureText] = useState<string>("");
    const [signatureHtml, setSignatureHtml] = useState<string>("");
    const [sendLimitPerHour, setSendLimitPerHour] = useState<number | ''>('');
    const [sendLimitPerDay, setSendLimitPerDay] = useState<number | ''>('');
    const [defaultBccAddressesInput, setDefaultBccAddressesInput] = useState<string>("");
    const [defaultReplyTo, setDefaultReplyTo] = useState<string>("");
    const [allowedFromAliasesInput, setAllowedFromAliasesInput] = useState<string>("");
    const [replyBehavior, setReplyBehavior] = useState<"reply" | "reply_all">("reply");
    const [openTrackingEnabled, setOpenTrackingEnabled] = useState<boolean>(true);
    const [clickTrackingEnabled, setClickTrackingEnabled] = useState<boolean>(false);
    const [unsubscribesEnabled, setUnsubscribesEnabled] = useState<boolean>(false);
    const [blockListInput, setBlockListInput] = useState<string>("");

    const [sendingWindowEnabled, setSendingWindowEnabled] = useState<boolean>(false);
    const [sendingWindowTimezone, setSendingWindowTimezone] = useState<string>("");
    const [sendingWindowDays, setSendingWindowDays] = useState<DayKey[]>(["mon", "tue", "wed", "thu", "fri"]);
    const [sendingWindowStartTime, setSendingWindowStartTime] = useState<string>("09:00");
    const [sendingWindowEndTime, setSendingWindowEndTime] = useState<string>("17:00");

    useEffect(() => {
        if (!selectedMemberIntegration) return;

        const options = selectedMemberIntegration?.options || {};
        const sendLimits = options?.send_limits || options?.sendLimits || {};
        const tracking = options?.tracking || {};
        const sendingWindow = options?.sending_window || options?.sendingWindow || {};
        const replyBehaviorOption = options?.reply_behavior ?? options?.replyBehavior;
        const resolvedReplyBehavior = replyBehaviorOption === "reply_all" ? "reply_all" : "reply";
        const defaultBcc = Array.isArray(options?.default_bcc_addresses) ? options.default_bcc_addresses : [];
        const allowedAliases = Array.isArray(options?.allowed_from_aliases) ? options.allowed_from_aliases : [];
        const resolvedBlockList = Array.isArray(options?.block_list)
            ? options.block_list
            : Array.isArray(options?.blocked_recipients)
                ? options.blocked_recipients
                : [];

        setIsDefaultForMember(!!selectedMemberIntegration?.is_default_for_member);
        setUseGmailSignature(!!(options?.use_gmail_signature ?? options?.useGmailSignature));
        setSignatureText(options?.email_signature_text || "");
        setSignatureHtml(options?.email_signature_html || "");
        setSendLimitPerHour(sendLimits?.per_hour ?? '');
        setSendLimitPerDay(sendLimits?.per_day ?? '');
        setDefaultBccAddressesInput(defaultBcc.join(", "));
        setDefaultReplyTo(options?.default_reply_to || "");
        setAllowedFromAliasesInput(allowedAliases.join(", "));
        setReplyBehavior(resolvedReplyBehavior);
        setOpenTrackingEnabled(!!(tracking?.open_tracking_enabled ?? tracking?.openTrackingEnabled ?? true));
        setClickTrackingEnabled(!!(tracking?.click_tracking_enabled ?? tracking?.clickTrackingEnabled ?? false));
        setUnsubscribesEnabled(!!(options?.unsubscribes_enabled ?? options?.unsubscribesEnabled ?? false));
        setBlockListInput(resolvedBlockList.join(", "));

        setSendingWindowEnabled(!!(sendingWindow?.enabled ?? false));
        setSendingWindowTimezone(sendingWindow?.timezone || workspace?.workspace_time_zone || "");
        setSendingWindowDays((sendingWindow?.days || ["mon", "tue", "wed", "thu", "fri"]) as DayKey[]);
        setSendingWindowStartTime(sendingWindow?.start_time || "09:00");
        setSendingWindowEndTime(sendingWindow?.end_time || "17:00");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMemberIntegration?.id, workspace?.workspace_time_zone]);

    useEffect(() => {
        if (isConnected) {
            refetchMemberIntegrations?.();
        }
    }, [isConnected, refetchMemberIntegrations]);

    const saveSettings = async () => {
        if (!workspace?.id || !selectedMemberIntegration?.id) return;

        if (sendLimitPerHour !== '' && sendLimitPerDay !== '' && Number(sendLimitPerHour) > Number(sendLimitPerDay)) {
            toast.error("Invalid send limits", { description: "Per-hour limit must be less than or equal to per-day limit." });
            return;
        }

        const existingOptions = selectedMemberIntegration?.options || {};

        const nextOptions: Record<string, any> = {
            ...existingOptions,
            use_gmail_signature: useGmailSignature,
            email_signature_text: signatureText || null,
            email_signature_html: signatureHtml || null,
            send_limits: {
                per_hour: sendLimitPerHour === '' ? null : Number(sendLimitPerHour),
                per_day: sendLimitPerDay === '' ? null : Number(sendLimitPerDay),
            },
            default_bcc_addresses: parseCsv(defaultBccAddressesInput),
            default_reply_to: defaultReplyTo || null,
            allowed_from_aliases: parseCsv(allowedFromAliasesInput),
            reply_behavior: replyBehavior,
            tracking: {
                open_tracking_enabled: openTrackingEnabled,
                click_tracking_enabled: clickTrackingEnabled,
            },
            unsubscribes_enabled: unsubscribesEnabled,
            block_list: parseCsv(blockListInput),
            sending_window: {
                enabled: sendingWindowEnabled,
                timezone: sendingWindowTimezone || null,
                days: sendingWindowDays,
                start_time: sendingWindowStartTime,
                end_time: sendingWindowEndTime,
            },
        };

        try {
            await updateMemberIntegrationMutation.mutateAsync({
                workspace_id: workspace.id,
                id: selectedMemberIntegration.id,
                is_default_for_member: isDefaultForMember,
                options: nextOptions,
            });
            await refetchMemberIntegrations?.();
            toast.success("Saved", { description: "Gmail settings updated." });
        } catch (e) {
            toast.error("Save failed", { description: "Could not update Gmail settings." });
        }
    };

    const toggleSendingDay = (day: DayKey, enabled: boolean) => {
        setSendingWindowDays((prevDays) => {
            if (enabled) {
                return Array.from(new Set([...prevDays, day]));
            }

            return prevDays.filter((existingDay) => existingDay !== day);
        });
    };

    return (
        <div className="rounded-lg border border-border">
            <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-4">
                <div>
                    <h3 className="text-[14px] font-semibold text-foreground">Advanced Gmail settings</h3>
                    <p className="mt-0.5 text-[12px] text-muted-foreground">
                        Optional controls for signatures, sending rules, and tracking. Most teams can leave defaults.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[12px]"
                    onClick={() => refetchMemberIntegrations?.()}
                    disabled={memberIntegrationsFetching}
                >
                    <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${memberIntegrationsFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <div className="space-y-4 px-4 py-4">
                {!isConnected ? (
                    <Alert className="border-border bg-muted/40">
                        <AlertDescription className="text-[12px] text-muted-foreground">
                            Connect Gmail to configure your personal settings.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {isConnected && gmailMemberIntegrations.length === 0 ? (
                    <Alert className="border-status-cancelled/20 bg-status-cancelled/5">
                        <AlertDescription className="text-[12px] text-muted-foreground">
                            No Gmail account found yet. If you just connected, click Refresh.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {gmailMemberIntegrations.length > 0 && selectedMemberIntegration ? (
                    <>
                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                            {gmailMemberIntegrations.length > 1 ? (
                                <div className="space-y-1.5 py-3">
                                    <Label className="text-[12px] text-muted-foreground">Connected account</Label>
                                    <Select
                                        value={selectedMemberIntegration?.id || ""}
                                        onValueChange={(value) => setSelectedMemberIntegrationId(value)}
                                    >
                                        <SelectTrigger className="h-8 text-[13px]">
                                            <SelectValue placeholder="Select Gmail account" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {gmailMemberIntegrations.map((integration: any) => (
                                                <SelectItem key={integration.id} value={integration.id}>
                                                    {integration.primary_email || integration.external_account_id || integration.id}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : null}

                            <GmailSettingRow
                                label="Default Gmail account"
                                description="Use this account as your default sender identity."
                            >
                                <Switch
                                    checked={isDefaultForMember}
                                    onCheckedChange={setIsDefaultForMember}
                                />
                            </GmailSettingRow>
                        </div>

                        <Accordion type="single" collapsible className="rounded-lg border border-border px-4">
                            <AccordionItem value="advanced-gmail-controls" className="border-b-0">
                                <AccordionTrigger className="py-3 text-[13px] font-medium text-foreground hover:no-underline">
                                    Advanced delivery and tracking controls
                                </AccordionTrigger>
                                <AccordionContent className="space-y-4 pb-3">
                                    <div className="flex gap-1 border-b border-border">
                                        {[
                                            { key: "general", label: "General" },
                                            { key: "sending", label: "Sending Rules" },
                                            { key: "safety", label: "Safety & Tracking" },
                                        ].map((tab) => (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => setActiveTab(tab.key as "general" | "sending" | "safety")}
                                                className={cn(
                                                    "px-2.5 py-1.5 text-[12px] font-medium transition-colors border-b-2 -mb-px",
                                                    activeTab === tab.key
                                                        ? "border-primary text-foreground"
                                                        : "border-transparent text-muted-foreground hover:text-foreground",
                                                )}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {activeTab === "general" ? (
                                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                                            <GmailSettingRow
                                                label="Use Gmail signature"
                                                description="Automatically use the signature configured in Gmail."
                                            >
                                                <Switch
                                                    checked={useGmailSignature}
                                                    onCheckedChange={setUseGmailSignature}
                                                />
                                            </GmailSettingRow>

                                            {!useGmailSignature ? (
                                                <div className="space-y-3 py-3">
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Signature (text)</Label>
                                                        <Textarea
                                                            value={signatureText}
                                                            onChange={(event) => setSignatureText(event.target.value)}
                                                            placeholder={"Best,\nJane"}
                                                            className="min-h-[100px] text-[13px]"
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Signature (HTML)</Label>
                                                        <Textarea
                                                            value={signatureHtml}
                                                            onChange={(event) => setSignatureHtml(event.target.value)}
                                                            placeholder={"<p>Best,<br/>Jane</p>"}
                                                            className="min-h-[100px] text-[13px]"
                                                        />
                                                    </div>
                                                </div>
                                            ) : null}

                                            <div className="grid gap-3 py-3 md:grid-cols-2">
                                                <div className="space-y-1.5">
                                                    <Label className="text-[12px] text-muted-foreground">Default BCC addresses</Label>
                                                    <Input
                                                        value={defaultBccAddressesInput}
                                                        onChange={(event) => setDefaultBccAddressesInput(event.target.value)}
                                                        placeholder="archive@acme.com, manager@acme.com"
                                                        className="h-8 text-[13px]"
                                                    />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-[12px] text-muted-foreground">Default reply-to address</Label>
                                                    <Input
                                                        value={defaultReplyTo}
                                                        onChange={(event) => setDefaultReplyTo(event.target.value)}
                                                        placeholder="support@acme.com"
                                                        className="h-8 text-[13px]"
                                                    />
                                                </div>
                                                <div className="space-y-1.5 md:col-span-2">
                                                    <Label className="text-[12px] text-muted-foreground">Allowed from aliases</Label>
                                                    <Input
                                                        value={allowedFromAliasesInput}
                                                        onChange={(event) => setAllowedFromAliasesInput(event.target.value)}
                                                        placeholder="sales@acme.com, hello@acme.com"
                                                        className="h-8 text-[13px]"
                                                    />
                                                </div>
                                                <div className="space-y-1.5 md:col-span-2">
                                                    <Label className="text-[12px] text-muted-foreground">Reply behavior</Label>
                                                    <Select value={replyBehavior} onValueChange={(value) => setReplyBehavior((value as "reply" | "reply_all") || "reply")}>
                                                        <SelectTrigger className="h-8 text-[13px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="reply">Reply (to sender only)</SelectItem>
                                                            <SelectItem value="reply_all">Reply all (to all recipients)</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    {activeTab === "sending" ? (
                                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                                            <GmailSettingRow
                                                label="Restrict sending window"
                                                description="Only send emails during specific days and times."
                                            >
                                                <Switch
                                                    checked={sendingWindowEnabled}
                                                    onCheckedChange={setSendingWindowEnabled}
                                                />
                                            </GmailSettingRow>

                                            {sendingWindowEnabled ? (
                                                <div className="space-y-3 py-3">
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Timezone</Label>
                                                        <Input
                                                            value={sendingWindowTimezone}
                                                            onChange={(event) => setSendingWindowTimezone(event.target.value)}
                                                            placeholder="America/New_York"
                                                            className="h-8 text-[13px]"
                                                        />
                                                    </div>

                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Allowed days</Label>
                                                        <div className="flex flex-wrap gap-3">
                                                            {DAY_OPTIONS.map((day) => (
                                                                <label key={day.value} className="flex items-center gap-2">
                                                                    <Checkbox
                                                                        checked={sendingWindowDays.includes(day.value)}
                                                                        onCheckedChange={(checked) => toggleSendingDay(day.value, Boolean(checked))}
                                                                    />
                                                                    <span className="text-[12px] text-foreground">{day.label}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="grid gap-3 sm:grid-cols-2">
                                                        <div className="space-y-1.5">
                                                            <Label className="text-[12px] text-muted-foreground">Start time</Label>
                                                            <Input
                                                                type="time"
                                                                value={sendingWindowStartTime}
                                                                onChange={(event) => setSendingWindowStartTime(event.target.value)}
                                                                className="h-8 text-[13px]"
                                                            />
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <Label className="text-[12px] text-muted-foreground">End time</Label>
                                                            <Input
                                                                type="time"
                                                                value={sendingWindowEndTime}
                                                                onChange={(event) => setSendingWindowEndTime(event.target.value)}
                                                                className="h-8 text-[13px]"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}

                                            <div className="space-y-1.5 py-3">
                                                <p className="text-[13px] text-foreground">Daily limits</p>
                                                <p className="text-[11px] text-muted-foreground">Recommended: 250/day, 200/hour.</p>
                                                <div className="grid gap-3 sm:grid-cols-2">
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Per hour</Label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={1000}
                                                            value={sendLimitPerHour}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                setSendLimitPerHour(value === "" ? "" : Number(value));
                                                            }}
                                                            className="h-8 text-[13px]"
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Per day</Label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={1000}
                                                            value={sendLimitPerDay}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                setSendLimitPerDay(value === "" ? "" : Number(value));
                                                            }}
                                                            className="h-8 text-[13px]"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    {activeTab === "safety" ? (
                                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                                            <GmailSettingRow label="Open tracking" description="Track email opens for outbound sends.">
                                                <Switch
                                                    checked={openTrackingEnabled}
                                                    onCheckedChange={setOpenTrackingEnabled}
                                                />
                                            </GmailSettingRow>
                                            <GmailSettingRow label="Click tracking" description="Track link clicks in sent emails.">
                                                <Switch
                                                    checked={clickTrackingEnabled}
                                                    onCheckedChange={setClickTrackingEnabled}
                                                />
                                            </GmailSettingRow>
                                            <GmailSettingRow label="Unsubscribe links" description="Include unsubscribe links in supported messages.">
                                                <Switch
                                                    checked={unsubscribesEnabled}
                                                    onCheckedChange={setUnsubscribesEnabled}
                                                />
                                            </GmailSettingRow>
                                            <div className="space-y-1.5 py-3">
                                                <Label className="text-[12px] text-muted-foreground">Block recipients</Label>
                                                <Input
                                                    value={blockListInput}
                                                    onChange={(event) => setBlockListInput(event.target.value)}
                                                    placeholder="no-reply@acme.com, blocked@acme.com"
                                                    className="h-8 text-[13px]"
                                                />
                                                <p className="text-[11px] text-muted-foreground">
                                                    Comma-separated email addresses that should never receive email.
                                                </p>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            size="sm"
                                            className="h-7 text-[12px]"
                                            onClick={() => { saveSettings(); }}
                                            disabled={!selectedMemberIntegration?.id || updateMemberIntegrationMutation.isLoading}
                                        >
                                            {updateMemberIntegrationMutation.isLoading ? "Saving..." : "Save Settings"}
                                        </Button>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </>
                ) : null}
            </div>
        </div>
    );
};

// Member-level IntegrationApp provider for Gmail
const MemberLevelIntegrationProvider = ({ children }: { children: React.ReactNode }) => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);

    const fetchMemberLevelToken = async () => {
        if (workspace?.id) {
            try {
                const response = await generateIntegrationAppToken({
                    workspace_id: workspace.id,
                    isMemberLevel: true
                });
                return response?.data?.token;
            } catch (error) {
                console.error('Failed to fetch member-level integration token:', error);
                return null;
            }
        }
        return null;
    };

    return (
        <IntegrationAppProvider fetchToken={fetchMemberLevelToken}>
            {children}
        </IntegrationAppProvider>
    );
};

// Component that uses member-level IntegrationApp context
const GmailPageContent = ({
    isConnected,
    connectedAt,
    integrationLoading,
    integrationId,
    onConnect,
    connectionData,
    setConnectionData,
    setIsConnected,
    setConnectedAt,
    setIntegrationLoading
}: GmailPageContentProps) => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);
    const provider = getProvider('gmail');
    const integrationApp = useIntegrationApp();
    const navigate = useNavigate();
    const { data: user }: any = useCurrentUser({});

    // Sync-configuration management used to fully activate the inbound sync on connect
    const createSyncConfigMutation = useCreateSyncConfiguration();
    const updateSyncConfigMutation = useUpdateSyncConfiguration();
    const { refetch: refetchSyncConfigurations } = useSyncConfigurations({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    } as any);

    const {
        data: memberIntegrations,
    }: any = useMemberIntegrationsMe({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    });

    const gmailMemberIntegrations = useMemo(() => (memberIntegrations || [])
        .filter((mi: any) => normalizeProvider(mi?.provider) === "gmail"), [memberIntegrations]);

    const selectedMemberIntegration = useMemo(() => (
        gmailMemberIntegrations.find((mi: any) => mi?.is_default_for_member)
        || gmailMemberIntegrations[0]
        || null
    ), [gmailMemberIntegrations]);

    const { data: workspaceMembersData }: any = useWorkspaceMembers({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    });

    const currentWorkspaceMember = useMemo(() => {
        if (!user?.email) return null;
        const members = workspaceMembersData?.members || workspaceMembersData?.users || [];
        const needle = String(user.email).toLowerCase().trim();
        return members.find((m: any) => String(m?.email || "").toLowerCase().trim() === needle) || null;
    }, [workspaceMembersData, user?.email]);

    const connectedEmail = selectedMemberIntegration?.primary_email || selectedMemberIntegration?.external_account_id;
    const connectedDateLabel = connectedAt
        ? new Date(connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : null;
    const [expandedObjects, setExpandedObjects] = useState<Record<string, boolean>>({});

    const { data: aiMemoryData, refetch: refetchAIMemory }: any = useAIMemoryObjects({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id && isConnected,
        staleTime: 0,
        refetchOnMount: "always",
        refetchInterval: isConnected ? 5000 : false,
    } as any);

    const aiMemoryEmailObject = useMemo(() => {
        if (!Array.isArray(aiMemoryData?.data)) return null;
        return aiMemoryData.data.find((item: any) => item?.api_slug === "conversation_messages") || null;
    }, [aiMemoryData]);

    const { data: emailFlowEnabled = false, isFetching: checkingEmailFlowStatus } = useQuery({
        queryKey: ["gmail-email-flow-status", workspace?.id, integrationId, provider.key],
        enabled: !!workspace?.id && !!integrationId && isConnected,
        staleTime: 0,
        refetchOnMount: "always",
        refetchInterval: isConnected ? 5000 : false,
        queryFn: async () => {
            const [firstFlowResult, receiveFlowResult] = await Promise.allSettled([
                integrationApp.connection(provider.key).flow("first-sync-for-emails").get(),
                integrationApp.connection(provider.key).flow("receive-email-events").get(),
            ]);

            const firstEnabled = firstFlowResult.status === "fulfilled" && !!firstFlowResult.value?.enabled;
            const receiveEnabled = receiveFlowResult.status === "fulfilled" && !!receiveFlowResult.value?.enabled;
            return firstEnabled || receiveEnabled;
        },
    });

    const syncRow = useMemo(() => {
        const aiEnabled = aiMemoryEmailObject?.status === "enabled";
        const enabled = aiEnabled && emailFlowEnabled;
        const fieldCount = aiMemoryEmailObject?.field_count || 0;
        return {
            key: "emails",
            label: "Emails",
            enabled,
            configured: aiEnabled,
            errors: 0,
            mappedFields: fieldCount,
            lastSyncLabel: connectedAt
                ? new Date(connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                : null,
        };
    }, [aiMemoryEmailObject, emailFlowEnabled, connectedAt]);

    const toggleExpand = (key: string) => {
        setExpandedObjects((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const identifyConnectedEmail = (email: string) => {
        const accountId = workspace?.id;
        const resolvedUserId = user?.user_id || email;

        if (!accountId || !resolvedUserId) {
            return;
        }

        const accountName = workspace?.workspace_name || workspace?.name || workspace?.unique_identifier || accountId;
        const userName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.name || email;

        try {
            hyperengage?.identify_account({
                account_id: accountId,
                user_id: resolvedUserId,
                traits: {
                    name: accountName,
                },
            } as any);

            hyperengage?.identify_user({
                user_id: resolvedUserId,
                traits: {
                    name: userName,
                    email,
                },
            });
        } catch (error) {
            console.error("Failed to identify Gmail user in Hyperengage:", error);
        }
    };

    const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(true);

    // Check for existing connection on component mount
    useEffect(() => {
        const checkConnection = async () => {
            try {
                const integration = await integrationApp.integration(provider.key).get();
                const existingConnection = integration?.connection;
                if (existingConnection && (!existingConnection.disconnected)) {
                    setConnectionData(integration);
                    setIsConnected(true);
                    // Best-effort connection timestamp
                    const ts = (existingConnection as any)?.connectedAt || (integration as any)?.createdAt || (integration as any)?.updatedAt || new Date().toISOString();
                    setConnectedAt(ts);
                }
            } catch (error) {
                // no existing connection
            } finally {
                setIsCheckingConnection(false);
            }
        };

        checkConnection();
    }, [integrationApp, provider.key, setConnectionData, setIsConnected, setConnectedAt]);

    const gmailClick = async () => {
        setIntegrationLoading(true);
        const ensureInfra = async (connectionId?: string) => {
            const connectionSelector = connectionId || provider.key;
            try {
                await Promise.allSettled([
                    integrationApp.connection(connectionSelector).dataSource('emails').get({ autoCreate: true }),
                ]);

                await Promise.allSettled([
                    integrationApp.connection(connectionSelector).fieldMapping('emails').get({ autoCreate: true }),
                ]);
            } catch (e) {
                // Best-effort provisioning; log but do not block UX
                console.error('Failed to ensure data sources/field mappings for connection', provider.key, e);
            }
        };

        const updateMemberIntegrationWithEmail = async (connectionId?: string) => {
            const connectionSelector = connectionId || provider.key;
            try {
                // Get email using custom action
                const getEmailResponse = await integrationApp.connection(connectionSelector).action('get-user-email').run();
                let extractedEmail = (getEmailResponse as any)?.output?.data?.emailAddress;

                console.log('extractedEmail', extractedEmail);

                // If get-user-email yields nothing, fall back to an identifier from the
                // connection itself. We must NOT skip backend creation in that case —
                // the canonical upsert below still needs to run.
                let fallbackEmail = '';
                if (!extractedEmail) {
                    try {
                        const connectionObj: any = await integrationApp.connection(connectionSelector).get();
                        fallbackEmail =
                            connectionObj?.primary_email ||
                            connectionObj?.externalAccountId ||
                            connectionObj?.accountData?.email ||
                            connectionObj?.accountData?.emailAddress ||
                            connectionObj?.accountData?.primaryEmail ||
                            connectionObj?.name ||
                            '';
                    } catch (e) {
                        // best-effort only — fall through to the synthesized identifier below
                    }
                    console.warn('Could not get email from Gmail integration; using fallback', fallbackEmail);
                }

                // Best available identifier for the backend upsert. Never empty so the
                // member-integration row is created regardless of get-user-email result.
                const resolvedEmail = extractedEmail || fallbackEmail || `gmail@${connectionSelector}`;

                if (extractedEmail) {
                    identifyConnectedEmail(extractedEmail);
                }

                if (workspace?.id) {
                    // Local create/update path (kept for backwards compatibility). Only
                    // attempt the local upsert when we actually resolved a real email.
                    if (extractedEmail || fallbackEmail) {
                        const localEmail = extractedEmail || fallbackEmail;
                        // Get member integrations to find the one to update
                        const memberIntegrationsResponse = await getMemberIntegrationsMe({ workspace_id: workspace.id });
                        const fetchedMemberIntegrations = memberIntegrationsResponse?.data || [];

                        const gmailMemberIntegration = fetchedMemberIntegrations.find((mi: any) =>
                            normalizeProvider(mi?.provider) === "gmail" &&
                            (mi?.is_default_for_member || mi?.primary_email === null || mi?.external_account_id === null)
                        );

                        if (gmailMemberIntegration && gmailMemberIntegration.id) {
                            await updateMemberIntegration({
                                workspace_id: workspace.id,
                                id: gmailMemberIntegration.id,
                                primary_email: localEmail,
                                external_account_id: localEmail, // Use email as external_id if missing
                            });
                        } else if (currentWorkspaceMember?.id && integrationId) {
                            // Create new member integration if existing one not found or not matched
                            await createMemberIntegration({
                                workspace_id: workspace.id,
                                workspace_member_id: currentWorkspaceMember.id,
                                integration_id: integrationId,
                                provider: "gmail",
                                external_account_id: localEmail,
                                primary_email: localEmail,
                                is_active: true,
                                is_default_for_member: true,
                                options: {},
                            });
                            console.log('Created Gmail member integration with email:', localEmail);
                        } else {
                            console.warn('Cannot create member integration locally: missing workspace member ID or integration ID', {
                                memberId: currentWorkspaceMember?.id,
                                integrationId
                            });
                        }
                    }

                    // Canonical backend upsert — resolves the member from auth, auto-creates
                    // the parent Integrations row + WorkspaceMemberIntegration row, and enables
                    // the provider's AI-memory objects. Runs regardless of the local branch.
                    await notifyBackendOfConnection(workspace.id, 'gmail', connectionSelector, resolvedEmail, resolvedEmail);
                }

                // Invalidate member integrations cache to refresh UI
                queryClient.invalidateQueries(["member-integrations"]);
            } catch (error) {
                console.error('Failed to update/create member integration with email:', error);
            }
        };

        // Fully activate the inbound (emails) sync on connect — mirrors the manual
        // "Start sync" recipe so the user does not have to enable it by hand.
        // Falls back to a best-effort flow trigger if prerequisites can't be resolved.
        const activateEmailsSync = async (connectionId?: string) => {
            const slug = 'conversation_messages';
            try {
                // The backend auto-enables the AI-memory objects on connect; fetch them
                // fresh to obtain the object_definition id before activating.
                let destinationObjectId: string | undefined;
                try {
                    const refreshed: any = await refetchAIMemory?.();
                    const aiObjects = refreshed?.data?.data || [];
                    destinationObjectId = aiObjects.find((o: any) => o?.api_slug === slug)?.object_id;
                } catch (e) {
                    // best-effort
                }

                // Resolve the parent integration_id (the member integration's integration_id).
                let resolvedIntegrationId: string | undefined = integrationId;
                if (!resolvedIntegrationId && workspace?.id) {
                    try {
                        const resp: any = await getMemberIntegrationsMe({ workspace_id: workspace.id });
                        const list = resp?.data || [];
                        resolvedIntegrationId = list.find((mi: any) => normalizeProvider(mi?.provider) === 'gmail')?.integration_id;
                    } catch (e) {
                        // best-effort
                    }
                }

                // Fresh existing sync configs (so we reuse rather than duplicate).
                let existingSyncConfigs: any = [];
                try {
                    const refreshed: any = await refetchSyncConfigurations?.();
                    existingSyncConfigs = refreshed?.data ?? [];
                } catch (e) {
                    // best-effort
                }

                const activated = await activateInboundSyncForProvider({
                    integrationApp,
                    workspaceId: workspace?.id,
                    integrationId: resolvedIntegrationId,
                    providerKey: provider.key,
                    slug,
                    destinationObjectId,
                    sourceObject: provider.defaultSourceCollections?.conversation_messages,
                    createSyncConfig: (args) => createSyncConfigMutation.mutateAsync(args),
                    updateSyncConfig: (args) => updateSyncConfigMutation.mutateAsync(args),
                    existingSyncConfigs,
                });

                if (!activated) {
                    // Could not resolve sync-config/object id — fall back to best-effort.
                    await triggerBestEffortInitialSync(integrationApp, connectionId || provider.key);
                }
            } catch (e) {
                await triggerBestEffortInitialSync(integrationApp, connectionId || provider.key);
            }
        };

        if (isConnected) {
            try {
                const connection = await integrationApp.integration(provider.key).openNewConnection();
                if (connection && (connection as any).state === 'READY') {
                    console.log('connection', connection);
                    setConnectionData(connection);
                    setIsConnected(true);
                    setConnectedAt(new Date().toISOString());
                    const connectionId = (connection as any)?.id;
                    // Ensure data sources and field mappings exist
                    await ensureInfra(connectionId);
                    // Update member integration with connected email
                    await updateMemberIntegrationWithEmail(connectionId);
                    // Fully activate the inbound emails sync (with best-effort fallback)
                    await activateEmailsSync(connectionId);
                }
            } catch (error) {
                console.error("Connection failed:", error);
            }
        } else {
            try {
                const connection = await integrationApp.integration(provider.key).connect();
                if (connection && (connection as any).state === 'READY') {
                    setConnectionData(connection);
                    setIsConnected(true);
                    setConnectedAt(new Date().toISOString());
                    const connectionId = (connection as any)?.id;
                    // Run on first connect
                    await ensureInfra(connectionId);
                    // Update member integration with connected email
                    await updateMemberIntegrationWithEmail(connectionId);
                    // Fully activate the inbound emails sync (with best-effort fallback)
                    await activateEmailsSync(connectionId);
                }
            } catch (error) {
                console.error("Connection failed:", error);
            }
        }

        setIntegrationLoading(false);
    };

    const handleBack = () => {
        if (!workspace?.unique_identifier) {
            navigate(-1);
            return;
        }

        navigate(`/home/${workspace.unique_identifier}/settings/integrations`);
    };

    return (
        <IntegrationDetailShell
            integrationName={provider.label}
            connected={isConnected}
            credentialOk={isConnected}
            externalId={connectedEmail || connectionData?.id || integrationId || null}
            lastSync={connectedDateLabel}
            reconnectLoading={integrationLoading || isCheckingConnection}
            reconnectLabel={isConnected ? "Reconnect" : "Connect"}
            onReconnect={gmailClick}
            onBack={handleBack}
        >
            {!isConnected ? (
                <div className="rounded-lg border border-border p-4">
                    <p className="text-[13px] text-foreground">
                        Connect Gmail to start syncing your email conversations.
                    </p>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Once connected, configure and sync your Gmail objects below.
                    </p>
                </div>
            ) : null}

            {isCheckingConnection ? (
                <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
                    Checking connection...
                </div>
            ) : (
                <>
                    <div className="mb-4">
                        <h2 className="mb-3 text-[13px] font-medium text-foreground">Sync Objects</h2>
                        <div className="rounded-lg border border-border divide-y divide-border">
                            <div key={syncRow.key}>
                                <button
                                    type="button"
                                    onClick={() => toggleExpand(syncRow.key)}
                                    className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        {expandedObjects[syncRow.key] ? (
                                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                        ) : (
                                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                        )}
                                        <span className="text-[13px] font-medium text-foreground">{syncRow.label}</span>
                                        {!syncRow.enabled && (
                                            <span className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Disabled</span>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                                        {syncRow.enabled ? (
                                            <div className="flex items-center gap-1 text-status-active">
                                                <Check className="h-3 w-3" />
                                                <span>Healthy</span>
                                            </div>
                                        ) : checkingEmailFlowStatus ? (
                                            <div className="flex items-center gap-1">
                                                <span>Checking sync</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1 text-status-cancelled">
                                                <AlertTriangle className="h-3 w-3" />
                                                <span>Not enabled</span>
                                            </div>
                                        )}
                                        <span>{syncRow.mappedFields} fields</span>
                                        {syncRow.lastSyncLabel ? <span>Synced {syncRow.lastSyncLabel}</span> : null}
                                    </div>
                                </button>

                                {expandedObjects[syncRow.key] ? (
                                    <div className="px-4 pb-3 ml-7">
                                        <GmailNewFlow
                                            isConnected={isConnected}
                                            integrationId={integrationId}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <GmailMemberSettings isConnected={isConnected} />
                </>
            )}
        </IntegrationDetailShell>
    );
};

const GmailPage = () => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);
    const [integrationLoading, setIntegrationLoading] = useState<boolean>(false);
    const [connectionData, setConnectionData] = useState<any>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [connectedAt, setConnectedAt] = useState<string | null>(null);
    // Get current user for member-level integration
    const { data: user }: any = useCurrentUser({});
    // New hooks for sync configuration management
    const { data: gmailIntegration }: any = useGmailIntegration({
        workspaceId: workspace?.id,
        userId: user?.user_id,
        enabled: !!workspace?.id && !!user?.user_id
    });

    // Get the integration ID for AI Memory functionality
    const integrationId = gmailIntegration?.[0]?.id;

    return (
        <MemberLevelIntegrationProvider>
            <GmailPageContent
                isConnected={isConnected}
                connectedAt={connectedAt}
                integrationLoading={integrationLoading}
                integrationId={integrationId}
                onConnect={() => { }}
                connectionData={connectionData}
                setConnectionData={setConnectionData}
                setIsConnected={setIsConnected}
                setConnectedAt={setConnectedAt}
                setIntegrationLoading={setIntegrationLoading}
            />
        </MemberLevelIntegrationProvider>
    );
};

export default GmailPage;
